<template>
  <div class="category-chips-viewport">
    <div ref="scrollRow" class="category-chips-row" @scroll="updateScrollState">
      <span class="category-label" @click="expanded = !expanded">
        {{ translate.categories }} <span class="category-arrow" :class="{ 'category-arrow--expanded': expanded }">&#10095;</span>
      </span>
      <div class="category-items" :class="{ 'category-items--expanded': expanded }">
        <span
          v-for="(category, index) in categories"
          :key="category.id"
          class="category-item-wrapper"
        >
          <NuxtLink
            :to="chipTo(category)"
            class="category-chip"
            :class="{ 'category-chip--active': category.slug === activeSlug }"
          ><h2 class="category-chip-title">{{ category.name }}</h2></NuxtLink>
          <NuxtLink
            v-if="category.slug === activeSlug"
            :to="`/${route.params.language}/${props.basePath}`"
            class="category-close"
            aria-label="Remover filtro"
          >x</NuxtLink>
          <span v-if="index < categories.length - 1" class="category-separator">  </span>
        </span>
      </div>
    </div>
    <button
      v-show="showScrollHint"
      type="button"
      class="category-scroll-hint"
      aria-label="Ver mais categorias"
      @click="scrollRight"
    >
      <span class="category-scroll-hint-arrow">&#10095;</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import modelsTradutor from "@/tradutors/models";

interface Category {
  id: number;
  name: string;
  slug: string;
  modelCount: number;
}

const props = defineProps({
  categories: {
    type: Array as () => Category[],
    required: true,
  },
  activeSlug: {
    type: String as () => string | null,
    default: null,
  },
  basePath: {
    type: String,
    default: 'modelos',
  },
});

const route = useRoute();
const expanded = ref(false);
const translate = computed(() =>
  route.params.language === "pt-br"
    ? modelsTradutor.portugues
    : modelsTradutor.ingles
);

const isMobile = ref(false);

const scrollRow = ref<HTMLElement | null>(null);
const canScrollRight = ref(false);

// Indicador só faz sentido acima de 769px (abaixo a lista quebra em linhas
// centralizadas, sem scroll horizontal) e quando ainda há conteúdo à direita.
const showScrollHint = computed(() => !isMobile.value && canScrollRight.value);

const updateScrollState = () => {
  const el = scrollRow.value;
  if (!el) return;
  // Tolerância de 4px para evitar oscilação por arredondamento no fim do scroll.
  canScrollRight.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 4;
};

const scrollRight = () => {
  const el = scrollRow.value;
  if (!el) return;
  el.scrollBy({ left: el.clientWidth * 0.7, behavior: "smooth" });
};

onMounted(() => {
  const mq = window.matchMedia("(max-width: 769px)");
  isMobile.value = mq.matches;
  if (isMobile.value && props.activeSlug) {
    expanded.value = true;
  }
  const handler = (e: MediaQueryListEvent) => {
    isMobile.value = e.matches;
    if (e.matches && props.activeSlug) {
      expanded.value = true;
    }
    nextTick(updateScrollState);
  };
  mq.addEventListener("change", handler);

  window.addEventListener("resize", updateScrollState);

  nextTick(updateScrollState);
  // Recalcula quando as fontes carregam (Bebas-Neue muda a largura dos chips).
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => nextTick(updateScrollState));
  }

  onBeforeUnmount(() => {
    mq.removeEventListener("change", handler);
    window.removeEventListener("resize", updateScrollState);
  });
});

watch(
  () => props.activeSlug,
  (slug) => {
    if (isMobile.value && slug) {
      expanded.value = true;
    }
    nextTick(updateScrollState);
  }
);

const chipTo = (category: Category) => {
  const base = `/${route.params.language}/${props.basePath}`;
  if (category.slug === props.activeSlug && isMobile.value) {
    return base;
  }
  return `${base}/categoria/${category.slug}`;
};
</script>

<style scoped lang="css">
.category-chips-viewport {
  position: relative;
  display: block;
}

.category-chips-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 37px 0 10px;
  gap: 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.category-chips-row::-webkit-scrollbar {
  display: none;
}

.category-label {
  font-family: "Bebas-Neue";
  font-size: 18px;
  color: #666666;
  font-weight: bold;
  white-space: nowrap;
  margin-right: 8px;
  flex-shrink: 0;
}

.category-arrow {
  color: #b20000;
  display: inline-block;
  transition: transform 0.3s;
  font-size: 10px;
  top: -4px;
  margin-left: 3px;  
}

.category-items {
  display: contents;
}

.category-item-wrapper {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
}

.category-chip {
  font-family: "Bebas-Neue";
  font-size: 17px;
  color: #666666;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s;
}

.category-chip:hover {
  color: #b20000;
}

.category-chip-title {
  display: inline;
  margin: 0;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
}

.category-chip--active {
  font-weight: bold;
}

.category-close {
  font-family: "Bebas-Neue";
  font-size: 15px;
  color: #666666;
  text-decoration: none;
  cursor: pointer;
  vertical-align: super;
  margin-left: 4px;
  transition: color 0.2s;
  top: -4px
}

.category-close:hover {
  color: #b20000;
}

.category-separator {
  font-family: "Bebas-Neue";
  font-size: 17px;
  color: #666666;
  margin: 0 6px;
}

/* Indicador de "role para ver mais": botão circular clicável na borda direita.
   Padding vertical espelha o da .category-chips-row para alinhar a seta aos chips. */
.category-scroll-hint {
  position: absolute;
  top: -5px;
  right: 0;
  bottom: 0;
  padding: 37px 0 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: none;
  cursor: pointer;
  background: transparent;
  z-index: 2;
}

.category-scroll-hint-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #b20000;
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.25);
  animation: category-hint-nudge 1.4s ease-in-out infinite;
}

@keyframes category-hint-nudge {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(3px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-scroll-hint-arrow {
    animation: none;
  }
}

@media screen and (max-width: 769px) {
  .category-scroll-hint {
    display: none;
  }

  .category-chips-row {
    flex-direction: column;
    align-items: center;
  }

  .category-label {
    cursor: pointer;
    margin-right: 0;
    margin-bottom: 8px;
    display: inline-flex;
    align-items: center;
  }

  .category-arrow {
    transform: rotate(0deg);
    margin-left: 8px;
    font-size: 14px;
    top: -3px;
  }

  .category-arrow--expanded {
    transform: rotate(90deg);
    margin-left: 8px;
    font-size: 14px;
  }

  .category-items {
    display: none;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 0;
  }

  .category-items--expanded {
    display: flex;
  }
}
</style>
